import { SOCIAL_HUB_VARIABLE, toPositiveInteger } from '@/config/social-hub';

export const LIVE_BUDGET_MISS = Object.freeze({ __socialHubLiveBudgetMiss: true });

export function withLiveBudget(promise, ms) {
  let timeoutId;
  return Promise.race([
    promise.finally(() => clearTimeout(timeoutId)),
    new Promise((resolve) => {
      timeoutId = setTimeout(() => resolve(LIVE_BUDGET_MISS), ms);
    }),
  ]);
}

/**
 * Daily-jittered Social Hub view-model.
 * Placeholder min/max ranges live in Optimizely (COD-878 / DE-3677) — not in source.
 */
export function useSocialHub() {
  const parseCitiesById = (featureVariables) => {
    const raw = featureVariables?.[SOCIAL_HUB_VARIABLE];
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
          ? parsed
          : {};
      } catch (err) {
        console.error('### Social Hub variables are not JSON', err?.message || err);
        return {};
      }
    }

    return raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  };

  const utcDateKey = () => new Date().toISOString().slice(0, 10);

  const unitFromSeed = (seed) => {
    let hash = 0;
    for (let i = 0; i < seed.length; i += 1) {
      hash = ((hash << 5) - hash) + seed.charCodeAt(i);
      hash = hash | 0;
    }
    return (Math.abs(hash) % 10000) / 10000;
  };

  const jitterBetween = (min, max, seed) => {
    if (!Number.isFinite(min) || !Number.isFinite(max)) {
      return null;
    }
    const low = Math.min(min, max);
    const high = Math.max(min, max);
    return Math.round(low + (unitFromSeed(seed) * (high - low)));
  };

  const jitterLiveCount = (liveCount, seed) => {
    const count = toPositiveInteger(liveCount);
    if (count === null) {
      return null;
    }
    const factor = 0.95 + (unitFromSeed(seed) * 0.1);
    return Math.max(1, Math.round(count * factor));
  };

  const isRange = (value) => Boolean(
    value
    && typeof value === 'object'
    && Number.isFinite(value.min)
    && Number.isFinite(value.max),
  );

  const hasValidSocialHubRow = (citiesById, cityId) => {
    if (!citiesById || typeof citiesById !== 'object' || Array.isArray(citiesById)) {
      return false;
    }

    const row = citiesById[String(cityId)];
    if (!row || typeof row !== 'object') {
      return false;
    }

    const {
      chatUsers,
      messages24h,
      bookingsLastMonth,
      soloTravellersPercent,
      busiestChatRoom,
      topNationalities,
    } = row;

    if (
      !isRange(chatUsers)
      || !isRange(messages24h)
      || !isRange(bookingsLastMonth)
      || !isRange(soloTravellersPercent)
      || !busiestChatRoom
      || typeof busiestChatRoom.name !== 'string'
      || !busiestChatRoom.name
      || !isRange(busiestChatRoom.staying)
      || !Array.isArray(topNationalities)
      || topNationalities.length < 3
    ) {
      return false;
    }

    return topNationalities.slice(0, 3).every(
      (item) => item
        && typeof item.countryCode === 'string'
        && /^[A-Z]{2}$/i.test(item.countryCode)
        && Number.isFinite(item.percent),
    );
  };

  const buildSocialHubViewModel = ({
    cityId,
    cityName,
    citiesById,
    liveMembersCount,
    dateKey,
  }) => {
    if (!hasValidSocialHubRow(citiesById, cityId)) {
      return null;
    }

    const row = citiesById[String(cityId)];
    const {
      chatUsers,
      messages24h,
      bookingsLastMonth,
      soloTravellersPercent,
      busiestChatRoom,
      topNationalities,
    } = row;

    const nationalities = topNationalities.slice(0, 3).map((item) => ({
      countryCode: item.countryCode.toUpperCase(),
      percent: Math.min(100, Math.max(0, Math.round(item.percent))),
    }));

    const liveCount = toPositiveInteger(liveMembersCount);
    if (liveCount === null) {
      return null;
    }

    const utc = typeof dateKey === 'string' && dateKey ? dateKey : utcDateKey();
    const travellersNow = jitterLiveCount(
      liveCount,
      `${utc}|${cityId}|now`,
    );
    const visitingNextMonth = jitterBetween(
      bookingsLastMonth.min,
      bookingsLastMonth.max,
      `${utc}|${cityId}|bookings`,
    );
    const chatUsersCount = jitterBetween(
      chatUsers.min,
      chatUsers.max,
      `${utc}|${cityId}|chat`,
    );
    const messagesCount = jitterBetween(
      messages24h.min,
      messages24h.max,
      `${utc}|${cityId}|msg`,
    );
    const soloPercentRaw = jitterBetween(
      soloTravellersPercent.min,
      soloTravellersPercent.max,
      `${utc}|${cityId}|solo`,
    );
    const busiestStaying = jitterBetween(
      busiestChatRoom.staying.min,
      busiestChatRoom.staying.max,
      `${utc}|${cityId}|busy`,
    );

    if (
      travellersNow === null
      || visitingNextMonth === null
      || chatUsersCount === null
      || messagesCount === null
      || soloPercentRaw === null
      || busiestStaying === null
    ) {
      return null;
    }

    return {
      cityName,
      travellersNow,
      visitingNextMonth,
      chatUsers: chatUsersCount,
      messages24h: messagesCount,
      soloPercent: Math.min(100, Math.max(0, soloPercentRaw)),
      busiestName: busiestChatRoom.name,
      busiestStaying,
      nationalities,
    };
  };

  const shouldSkipSocialHubLiveFetch = ({
    isHydrating,
    missedCityId,
    cachedViewModel,
    cityId,
  }) => {
    if (!isHydrating || cityId == null) {
      return false;
    }
    if (cachedViewModel && Number(cachedViewModel.cityId) === Number(cityId)) {
      return true;
    }
    if (missedCityId == null) {
      return false;
    }
    return Number(missedCityId) === Number(cityId);
  };

  return {
    buildSocialHubViewModel,
    hasValidSocialHubRow,
    parseCitiesById,
    shouldSkipSocialHubLiveFetch,
    utcDateKey,
  };
}
